<template>
  <div class="w-full h-full bg-white flex flex-col px-8 overflow-y-auto">
    <div class="py-8">
      <h2 class="font-semibold">Knowledge Base Explorer</h2>
      <p class="text-gray-dark text-sm mt-1">
        Search a knowledge base and see what gets retrieved. <b>Raw</b> runs the vector search directly;
        <b>Full RAG pipeline</b> runs the exact steps the bot runs — query reformulation, facet-driven
        filters, filtered search and zero-hit fallback — and shows every parameter.
      </p>
    </div>

    <div class="flex gap-4 items-end">
      <div class="flex flex-col gap-1 flex-1 min-w-0">
        <label class="font-medium">Knowledge base</label>
        <base-select :value="selectedIndexId" :options="indexOptions" class="h-10" @input="selectedIndexId = $event"/>
      </div>
      <div class="flex flex-col gap-1" style="width: 16rem">
        <label class="font-medium">Mode</label>
        <base-select :value="mode" :options="modeOptions" class="h-10" @input="mode = $event"/>
      </div>
      <div class="flex flex-col gap-1" style="width: 7rem">
        <label class="font-medium">Top N</label>
        <base-input v-model.number="topN" name="kb-topn" variant="editor" type="number" class="border-gray-border h-10"/>
      </div>
    </div>

    <div class="flex gap-4 items-end mt-4">
      <div class="flex flex-col gap-1 flex-1 min-w-0">
        <label class="font-medium">Query</label>
        <base-input v-model.trim="query" name="kb-query" variant="editor" class="border-gray-border h-10"
                    placeholder="e.g. white veja sneakers" @keyup.enter="search"/>
      </div>
      <busy-button id="kbSearchButton" variant="primary" class="w-40" :is-loading="isSearching" :is-disabled="!canSearch || isSearching" @click="search">
        Search
      </busy-button>
    </div>

    <div class="mt-6 mb-10">
      <div v-if="isSearching" class="text-gray">Searching…</div>

      <template v-else-if="results !== null">
        <div v-if="pipeline" class="border-solid border border-gray-border rounded p-4 mb-4 bg-soft">
          <h3 class="font-semibold text-sm mb-3">RAG pipeline</h3>

          <div class="text-sm mb-2">
            <span class="text-gray-dark">Reformulated query: </span>
            <span class="font-mono">{{ pipeline.reformulated_query }}</span>
          </div>

          <div v-if="pipeline.fell_back" class="text-sm font-medium mb-3">
            ⚠ Filters matched no documents — fell back to an unfiltered search.
          </div>

          <div class="mb-3">
            <div class="text-gray-dark text-sm mb-1">Available filters (from facets)</div>
            <p v-if="!pipeline.filterable_fields.length" class="text-gray text-sm">none for this knowledge base</p>
            <ul v-else class="text-sm pl-0">
              <li v-for="f in pipeline.filterable_fields" :key="f.collection + '.' + f.field" class="font-mono">
                {{ formatFacetField(f) }}
              </li>
            </ul>
          </div>

          <div class="mb-3">
            <div class="text-gray-dark text-sm mb-1">Proposed by the LLM</div>
            <pre class="bg-white border rounded p-2 text-xs overflow-x-auto">{{ formatPayload(pipeline.proposed_filters) }}</pre>
          </div>

          <div>
            <div class="text-gray-dark text-sm mb-1">Applied to the search (validated)</div>
            <pre class="bg-white border rounded p-2 text-xs overflow-x-auto">{{ formatPayload(pipeline.applied_filters) }}</pre>
          </div>
        </div>

        <div class="text-gray-dark text-sm mb-3">
          {{ results.length }} result{{ results.length === 1 ? '' : 's' }} for
          <span class="font-mono">{{ lastQuery }}</span>
          <span v-if="provider"> · provider: {{ provider }}</span>
        </div>

        <p v-if="!results.length" class="text-gray">No documents matched this query.</p>

        <div v-for="(result, i) in results" :key="result.id || i"
             class="border-solid border border-gray-border rounded p-4 mb-3">
          <div class="flex items-center justify-between text-sm">
            <span class="font-mono truncate" :title="result.id">{{ result.title || result.id || `#${i + 1}` }}</span>
            <span class="text-gray-dark flex-shrink-0 ml-3">score: {{ formatScore(result.score) }}</span>
          </div>
          <p v-if="result.content" class="text-sm text-gray-dark mt-2 whitespace-pre-wrap">{{ result.content }}</p>
          <pre class="bg-soft border rounded mt-2 p-2 text-xs overflow-x-auto">{{ formatPayload(result.payload) }}</pre>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import indexesService from '@/services/amio/indexes.service'
import BusyButton from '@/components/common/BusyButton'
import toaster from '@/utils/toaster'

export default {
  name: 'KnowledgeBaseExplorer',
  components: { BusyButton },

  data() {
    return {
      indexes: [],
      selectedIndexId: '',
      query: '',
      topN: 10,
      mode: 'raw',
      modeOptions: [
        { value: 'raw', text: 'Raw vector search' },
        { value: 'rag', text: 'Full RAG pipeline (LLM + filters)' }
      ],
      isSearching: false,
      results: null, // null = not searched yet; [] = searched, no matches
      provider: null,
      lastQuery: null,
      pipeline: null // rag-mode only: reformulated query, facets, proposed + applied filters, fell_back
    }
  },

  computed: {
    indexOptions() {
      // BaseSelect (t-rich-select) reads valueAttribute='value' + textAttribute='text' — a `label`
      // key renders blank options. Map the KB name to `text`, the index id to `value`.
      return this.indexes.map(index => ({ value: index.id, text: index.name || index.id }))
    },
    canSearch() {
      return Boolean(this.selectedIndexId) && Boolean(this.query && this.query.trim())
    }
  },

  methods: {
    formatScore(score) {
      return typeof score === 'number' ? score.toFixed(4) : '—'
    },

    formatPayload(payload) {
      return JSON.stringify(payload || {}, null, 2)
    },

    formatFacetField(field) {
      const head = `${field.field} (${field.type}, ${field.collection})`
      return field.values && field.values.length ? `${head}: ${field.values.join(', ')}` : head
    },

    async loadIndexes() {
      try {
        this.indexes = await indexesService.list()
      } catch (err) {
        toaster.error(err.message || err)
      }
    },

    async search() {
      if (!this.canSearch) return
      this.isSearching = true
      try {
        // an empty number input yields NaN via v-model.number — never send that as top_n
        const topN = Number.isInteger(this.topN) && this.topN > 0 ? this.topN : 10
        const response = await indexesService.searchKnowledgeBase(this.selectedIndexId, { query: this.query, topN, mode: this.mode })
        this.results = response.results || []
        this.provider = response.provider || null
        this.lastQuery = response.query || this.query
        this.pipeline = this.mode === 'rag' ? {
          reformulated_query: response.reformulated_query,
          filterable_fields: response.filterable_fields || [],
          proposed_filters: response.proposed_filters || [],
          applied_filters: response.applied_filters || {},
          fell_back: Boolean(response.fell_back)
        } : null
      } catch (err) {
        toaster.error(err.message || err)
      } finally {
        this.isSearching = false
      }
    }
  },

  async mounted() {
    await this.loadIndexes()
  }
}
</script>
